/**
 * Add a project lane, or edit one: name, DRI, support, ordering, active.
 *
 * One component for both, on the same reasoning as PersonEditor - the two forms
 * differ by one checkbox and by how the body is assembled, and splitting them would
 * duplicate the roster dropdown twice over.
 *
 * The two bodies are assembled differently and the difference is not an oversight.
 * Editing sends only what changed, because a stored row exists and everything absent
 * from the patch must be left alone; see PhaseEditor's header for what goes wrong
 * otherwise. Creating sends the whole form, because there is nothing to leave alone -
 * an omitted field takes the server's default, and every default is the honest one.
 *
 * DRI and Support are the two fields the workbook never had filled in. Eighteen
 * owner slots came across the migration null, because the name-per-project mapping
 * has never existed anywhere, and they were deliberately NOT guessed at load time.
 * This form is how they get filled in, one honest answer at a time, which is why the
 * roster dropdown offers "Unassigned" as a first-class choice rather than defaulting
 * to whoever happens to be first alphabetically.
 */

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';

import { useIdentity } from './AppShell';
import { createProject, describeError, patchProject } from '../services/api';
import { palette } from '../styles/theme';
import { ErrorText, Hint, Input, Label, PrimaryButton, SecondaryButton, Select } from '../styles/ui';
import type { PhaseCreate, Person, Project, ProjectPatch } from '../types';

interface FormValues {
  name: string;
  category: string;
  dri_email: string;
  support_email: string;
  active: boolean;
  /**
   * '' | 'yes' | 'no'. A select rather than a checkbox because the field has THREE
   * states and the third is load-bearing: '' is "nobody has classified this", which
   * is not the same as "we looked and it is not capital". A tick-box would have to
   * collapse those two, and the finance report counts them apart.
   */
  capex: string;
  /** Create only: seed the lane with the standard phases. See STANDARD_PHASES. */
  seed: boolean;
}

/** '' | 'yes' | 'no' as the API's three states. */
export function capexFromForm(value: string): boolean | null {
  if (value === 'yes') {
    return true;
  }
  if (value === 'no') {
    return false;
  }
  return null;
}

/** The API's three states as the form's. */
export function capexToForm(value: boolean | null | undefined): string {
  if (value === true) {
    return 'yes';
  }
  if (value === false) {
    return 'no';
  }
  return '';
}

/**
 * The six rows every project in the workbook had.
 *
 * Not an invention: six of the nine migrated lanes carry exactly this list in exactly
 * this order, and a seventh differs only by having no Wireframes row. Offering it as
 * a tick-box means a new lane arrives with the structure everyone already reads,
 * rather than empty and needing six trips through the phase form before the chart can
 * say anything about it.
 *
 * They are created with no dates and no progress, which is the point - the shape is
 * known, the schedule is not, and seeding invented dates is how the workbook filled up
 * with numbers nobody believed. Maintenance goes in structural, so it draws as an
 * ongoing-support band and is excluded from the completeness denominator rather than
 * being reported as six missing fields on day one.
 */
export const STANDARD_PHASES: readonly { name: string; structural: boolean }[] = [
  { name: 'Planning', structural: false },
  { name: 'Wireframes', structural: false },
  { name: 'Architecting', structural: false },
  { name: 'Coding', structural: false },
  { name: 'Testing', structural: false },
  { name: 'Maintenance', structural: true },
];

function seedPhases(): PhaseCreate[] {
  return STANDARD_PHASES.map((phase, index) => ({
    name: phase.name,
    phase_order: index,
    structural: phase.structural,
  }));
}

const Form = styled.form`
  display: grid;
  grid-template-columns: minmax(180px, 1.4fr) minmax(160px, 1fr) minmax(160px, 1fr);
  gap: 12px 14px;
  align-items: start;
  padding: 14px 16px 16px 30px;
`;

const Actions = styled.div`
  grid-column: 1 / -1;
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const Spacer = styled.div`
  flex: 1;
`;

const CheckLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  font-weight: 600;
  color: ${palette.ink};
  cursor: pointer;
`;

export function buildProjectPatch(
  values: FormValues,
  dirty: Partial<Record<keyof FormValues, boolean>>
): ProjectPatch {
  const patch: ProjectPatch = {};
  if (dirty.name) {
    patch.name = values.name.trim();
  }
  if (dirty.dri_email) {
    // The backend lowercases and turns '' into a real null, but doing it here too
    // means the value posted matches the value that comes back, so the lane does not
    // flicker from "Joe@..." to "joe@..." after a save.
    patch.dri_email = values.dri_email.trim().toLowerCase() || null;
  }
  if (dirty.support_email) {
    patch.support_email = values.support_email.trim().toLowerCase() || null;
  }
  if (dirty.active) {
    patch.active = values.active;
  }
  if (dirty.capex) {
    // Null is a real value here - it puts the project back to unreviewed - so this
    // sends the key whenever the field was touched, rather than only when it holds
    // something truthy. The API reads the key's presence, not its truthiness.
    patch.capex = capexFromForm(values.capex);
  }
  if (dirty.category) {
    // Trimmed here as well as on the server, so the value posted matches the value
    // that comes back and the group heading does not shift after a save. Empty string
    // rather than undefined: it has to reach the API, which turns it into a real null.
    patch.category = values.category.trim();
  }
  return patch;
}

/**
 * Two modes, and the callback differs between them because the API's answer does.
 *
 * PATCH returns ProjectOut, which has no phases, so an edit can only ever report the
 * patch and let the caller merge it field by field - see services/api.ts. POST returns
 * ProjectDetail, so a create hands back a whole lane ready to append. Expressing that
 * as a union rather than two optional callbacks makes using the wrong one a compile
 * error instead of a lane that quietly empties itself.
 */
export type ProjectEditorProps = {
  people: Person[];
  /**
   * Every category already in use on the roadmap, for the datalist below.
   *
   * Passed in rather than fetched, because the caller already has every project and a
   * second request to learn something it is holding would be a round trip for nothing.
   * Optional so the component still renders for a caller that has not got them yet.
   */
  categories?: string[];
  onCancel: () => void;
} & (
  | {
      project: Project;
      onSaved: (patch: ProjectPatch) => void;
      nextLaneOrder?: never;
      onCreated?: never;
    }
  | {
      project: null;
      /** Where the new lane sits in the row order - the API defaults it to 0. */
      nextLaneOrder: number;
      /**
       * Create it as work that has not started, so it opens in Upcoming rather than
       * on the roadmap. Set by which "+ Add" choice was taken, not by a field in this
       * form: by the time somebody is filling the form in they have already said
       * which kind of thing they are adding.
       */
      future?: boolean;
      onCreated: (project: Project) => void;
      onSaved?: never;
    }
);

/** The roster dropdown, shared by both owner fields. */
function OwnerOptions({ people, current }: { people: Person[]; current: string | null }) {
  const known = people.some((p) => p.email === current);
  return (
    <>
      <option value="">Unassigned</option>
      {!known && current ? <option value={current}>{current} (not on the roster)</option> : null}
      {people.map((person) => (
        <option key={person.email} value={person.email}>
          {person.name}
          {person.active ? '' : ' (inactive)'}
        </option>
      ))}
    </>
  );
}

export default function ProjectEditor(props: ProjectEditorProps) {
  const { project, people, categories = [], onCancel } = props;
  const [error, setError] = useState<string | null>(null);
  const identity = useIdentity();
  // Fails closed, like every flag here that mirrors a gate: an older backend leaves
  // it undefined and the field is shown but not editable. The API refuses anyway.
  const mayClassify = Boolean(identity?.may_classify);

  const {
    register,
    handleSubmit,
    formState: { dirtyFields, isSubmitting, isDirty, errors },
  } = useForm<FormValues>({
    defaultValues: {
      name: project?.name ?? '',
      category: project?.category ?? '',
      dri_email: project?.dri_email ?? '',
      support_email: project?.support_email ?? '',
      active: project?.active ?? true,
      capex: capexToForm(project?.capex),
      seed: true,
    },
  });

  const onSubmit = handleSubmit(async (values) => {
    setError(null);
    try {
      if (!project) {
        props.onCreated(
          await createProject({
            name: values.name.trim(),
            category: values.category.trim() || null,
            lane_order: props.nextLaneOrder,
            dri_email: values.dri_email.trim().toLowerCase() || null,
            support_email: values.support_email.trim().toLowerCase() || null,
            active: true,
            future: props.future ?? false,
            phases: values.seed ? seedPhases() : [],
          })
        );
        return;
      }

      const patch = buildProjectPatch(values, dirtyFields);
      if (Object.keys(patch).length === 0) {
        onCancel();
        return;
      }
      await patchProject(project.project_id, patch);
      // Merged field by field by the caller rather than replaced wholesale: the
      // PATCH response is ProjectOut and has no `phases`, so assigning it over the
      // lane would empty it. See services/api.ts.
      props.onSaved(patch);
    } catch (err) {
      setError(describeError(err));
    }
  });

  return (
    <Form onSubmit={onSubmit}>
      <Label>
        Project name
        <Input
          {...register('name', { required: 'A project needs a name.', maxLength: 200 })}
          aria-invalid={Boolean(errors.name)}
          placeholder={project ? undefined : 'e.g. Client Portal'}
          // Focused on create only. Auto-focusing the edit form would steal the
          // caret every time somebody opened a lane to read who the DRI is.
          autoFocus={!project}
        />
      </Label>

      {/*
        A picker of groups that already exist, not a free-text box.

        It was a text input with a datalist, which was the right answer while there was
        no other way to make a group: nobody could write the list in advance, so the
        list had to be authored by typing. Now that groups are made deliberately on the
        roadmap - "New group", then choose what goes in it - the name is written exactly
        once, and every place that reads it is choosing from what exists.

        That closes the gap the datalist could only narrow. A datalist SUGGESTS; it does
        not stop somebody typing past it, so "Data", "data" and "DATA" were still three
        headings a hurried afternoon away. A select cannot produce a fourth spelling.

        "No group" is a first-class option rather than an empty string somebody has to
        clear the box to reach, for the same reason the owner dropdowns offer
        "Unassigned": an unfiled project is a real state and the roadmap has a place for
        it - see UNGROUPED in utils/laneView.ts.
      */}
      <Label>
        Group
        <Select {...register('category')}>
          <option value="">No group</option>
          {/* A value stored before this list was built - or one whose group has since
              been emptied - is offered back rather than silently reset to "No group"
              by a select that cannot represent it. Same guard as OwnerOptions. */}
          {project?.category && !categories.includes(project.category) ? (
            <option value={project.category}>{project.category}</option>
          ) : null}
          {categories.map((category) => (
            <option key={category} value={category}>
              {category}
            </option>
          ))}
        </Select>
      </Label>
      <Hint>
        {categories.length === 0
          ? 'No groups yet — make one with “New group” above the chart.'
          : 'Groups this lane with others like it on the roadmap.'}
      </Hint>

      <Label>
        DRI
        <Select {...register('dri_email')}>
          <OwnerOptions people={people} current={project?.dri_email ?? null} />
        </Select>
        <Hint>Directly responsible individual.</Hint>
      </Label>

      <Label>
        Support
        <Select {...register('support_email')}>
          <OwnerOptions people={people} current={project?.support_email ?? null} />
        </Select>
      </Label>

      {/* How it is classified for capitalisation.

          SHOWN TO EVERYBODY, editable by admins and finance leads. Disabled rather
          than hidden on the same argument the phase dates use: whether a lane is
          capital is a fact people need to read, and hiding it from everyone who
          cannot change it would make the commonest question unanswerable by most of
          the firm.

          Only on edit. A project is created unclassified, because nobody has looked
          at a lane that does not exist yet - and offering the question on the create
          form would get it answered reflexively by whoever happened to be making the
          project. */}
      {project ? (
        <Label>
          Capitalisation
          <Select
            {...register('capex')}
            disabled={!mayClassify}
            title={
              mayClassify
                ? undefined
                : 'Admins and finance leads set this. An admin can grant that on the Team page.'
            }
          >
            <option value="">Not reviewed</option>
            <option value="yes">Capex</option>
            <option value="no">Not capex</option>
          </Select>
          <Hint>Which phases then count is policy, shown on the Finance page.</Hint>
        </Label>
      ) : null}

      <Actions>
        {project ? (
          <>
            <CheckLabel>
              <input type="checkbox" {...register('active')} />
              Active on the roadmap
            </CheckLabel>
            <Hint>Unticking hides the lane. Nothing is deleted.</Hint>
          </>
        ) : (
          <>
            <CheckLabel>
              <input type="checkbox" {...register('seed')} />
              Start with the standard phases
            </CheckLabel>
            {/* Named in full rather than left as "the standard phases", because the
                tick-box is ticked by default and somebody should be able to untick it
                knowing exactly what they are declining. */}
            <Hint>
              Planning, Wireframes, Architecting, Coding, Testing and a Maintenance band -
              no dates, ready to fill in.
            </Hint>
          </>
        )}
        <Spacer />
        {errors.name ? <ErrorText>{errors.name.message}</ErrorText> : null}
        {error ? <ErrorText role="alert">{error}</ErrorText> : null}
        <SecondaryButton type="button" onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </SecondaryButton>
        {/* `isDirty` gates saving an edit that changed nothing, but must NOT gate a
            create: the form starts pristine and a lane named from the placeholder is
            still a lane somebody wants. Required-field validation catches the empty
            case instead. */}
        <PrimaryButton type="submit" disabled={isSubmitting || (project !== null && !isDirty)}>
          {isSubmitting ? 'Saving…' : project ? 'Save project' : 'Create project'}
        </PrimaryButton>
      </Actions>
    </Form>
  );
}
